import React, { ReactNode, useMemo } from 'react';
import Tooltip2 from 'components/Common/Tooltip2';
import Icon from 'components/Utils/NewIcon';
import DocumentUtils, { DocumentHistoryEntry } from 'services/DocumentUtils';
import { useAppSelector } from 'careand-redux/store';
import { PatientDocument, Patient } from 'generated/api';
import './style.scss';

type Props = {
  document: PatientDocument;
  patient?: Patient;
  width?: string;
  placement?: 'right' | 'bottom';
  // Custom hover trigger. Defaults to a circle-info icon when omitted.
  children?: ReactNode;
};

/**
 * The tooltip body on its own, mirroring AppointmentStatusHistoryContent, so it
 * can be dropped into any tooltip mechanism.
 */
export const DocumentStatusHistoryContent = ({ entries }: { entries: DocumentHistoryEntry[] }) => (
  <div className="document-status-history">
    {entries.map((entry, index) => (
      <div className="dsh-entry" key={index}>
        <div className="dsh-entry-head">
          <span className="dsh-action">{entry.actionLabel}</span>
          <span className="dsh-date">{entry.date}</span>
        </div>
        <div className="dsh-meta">
          <span className="dsh-who">{entry.byWhom}</span>
          {entry.details.map((detail, i) => (
            <span className="dsh-detail" key={i}>
              {detail}
            </span>
          ))}
        </div>
      </div>
    ))}
  </div>
);

/**
 * Hoverable trigger that renders a document's server-authored change history
 * (who did what, when) as a readable timeline inside a careand-tooltip.
 * Mirrors AppointmentStatusHistory. Staff-only: the backend strips
 * status_history from patient-role responses, and this renders nothing when
 * the trail is absent/empty (all documents predating the feature).
 */
const DocumentStatusHistory = ({
  document,
  patient,
  width = '20px',
  placement = 'bottom',
  children,
}: Props) => {
  const physicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);

  const entries = useMemo(
    () => DocumentUtils.getStatusHistoryEntries(document, physicians, admins, patient),
    [document, physicians, admins, patient],
  );

  if (!entries.length) return null;

  return (
    <Tooltip2 placement={placement} tooltip={<DocumentStatusHistoryContent entries={entries} />}>
      {(register) => (
        <span className="dsh-trigger" {...register}>
          {children ?? <Icon src="circle-info" width={width} />}
        </span>
      )}
    </Tooltip2>
  );
};

export default DocumentStatusHistory;
